/**
 * EditGamePage — the shared admin "Edit a Game" screen at /games/:gameId/edit.
 *
 * One page, two entry points: the "Edit" action on the Manage Division Games page
 * and the "Resolve" action on a disputed game both land here. It reuses the same
 * building blocks as "Record a game" (SlotEditor + ScoreEntry), pre-filled from
 * the existing game, so correcting a schedule or a score looks and validates the
 * same everywhere.
 *
 * Saving calls the admin update endpoint. Because supplying a result flips a game
 * to COMPLETED (reversing/recomputing standings), editing a DISPUTED game's score
 * here is exactly what resolves the dispute — no separate resolve step is needed.
 *
 * A `?returnTo=` query param controls where Cancel/Save return to (the Manage
 * Games list, or the game detail page for the Resolve flow); it defaults to the
 * game's detail page.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import { fetchGameById, adminUpdateGame } from '../../store/slices/gameSlice';
import { fetchLeagueSeason } from '../../store/slices/leagueSlice';
import { GameStatus, UserRole } from '@sulis-liiga/common';
import type { AdminUpdateGameRequest, SetPair } from '@sulis-liiga/common';
import SlotEditor, {
  emptySlot,
  isSlotComplete,
  isDateOutsideSeason,
  type SlotInput,
} from '../../components/game/SlotEditor';
import ScoreEntry, { type ScoreEntryResult } from '../../components/game/ScoreEntry';
import GameNotesField from '../../components/game/GameNotesField';
import { teamLabelsOf, scoreFor } from '../../utils/gameTeams';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';

const toISODate = (d: string | Date): string => new Date(d).toISOString().slice(0, 10);

const EditGamePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { gameId } = useParams<{ gameId: string }>();
  const [searchParams] = useSearchParams();

  const { currentGame: game, loading, error } = useSelector((state: RootState) => state.game);
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { selectedLeagueId } = useSelector((state: RootState) => state.ui);
  const { currentSeason } = useSelector((state: RootState) => state.league);

  const isAdmin =
    isAuthenticated &&
    (user?.roles?.includes(UserRole.LEAGUE_ADMINISTRATOR) ||
      user?.roles?.includes(UserRole.SUPER_ADMINISTRATOR));

  // Where Cancel/Save return to — the caller passes ?returnTo=, else the game page.
  const returnTo = searchParams.get('returnTo') || (gameId ? `/games/${gameId}` : '/games');

  const { resolved: tenantResolved } = useActiveLeagueTenant(undefined);

  const [slot, setSlot] = useState<SlotInput>(emptySlot());
  const [editScore, setEditScore] = useState(false);
  const [result, setResult] = useState<ScoreEntryResult | null>(null);
  const [notes, setNotes] = useState('');
  const [ready, setReady] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (gameId && tenantResolved) {
      dispatch(fetchGameById(gameId));
    }
  }, [dispatch, gameId, tenantResolved]);

  // Load the game's season so the played date can be constrained to it.
  useEffect(() => {
    if (game?.seasonId && selectedLeagueId) {
      dispatch(fetchLeagueSeason({ leagueId: selectedLeagueId, seasonId: game.seasonId }));
    }
  }, [dispatch, game?.seasonId, selectedLeagueId]);

  // Seed the form from the loaded game exactly once (when it first arrives for
  // this id); subsequent edits are owned by the form state.
  useEffect(() => {
    if (!game || game.id !== gameId || ready) return;
    setSlot({
      date: game.scheduledDate ? toISODate(game.scheduledDate) : '',
      time: game.scheduledTime ?? '',
      venueName: game.venue?.name ?? '',
      venueAddress: game.venue?.address ?? '',
    });
    // A game that already has a result opens with the score section on and
    // pre-filled; a game without one (pending/scheduled) opens schedule-only.
    setEditScore(!!game.result);
    setReady(true);
  }, [game, gameId, ready]);

  const [teamAId, teamBId] = game?.teamIds ?? ['', ''];
  const [teamALabel, teamBLabel] = game ? teamLabelsOf(game) : ['Team A', 'Team B'];

  // Positional pre-fill for the score grid, mapped from the id-keyed result.
  const initialSets: SetPair[] | undefined = useMemo(() => {
    if (!game?.result) return undefined;
    return game.result.sets.map(s => ({ a: scoreFor(s, teamAId), b: scoreFor(s, teamBId) }));
  }, [game?.result, teamAId, teamBId]);

  const initialRetirement = useMemo(() => {
    const retiredId = game?.result?.retiredTeamId;
    if (!retiredId) return undefined;
    return { isRetirement: true, retiredSide: (retiredId === teamAId ? 'a' : 'b') as 'a' | 'b' };
  }, [game?.result?.retiredTeamId, teamAId]);

  const seasonBounds = useMemo(() => {
    if (currentSeason && game && currentSeason.id === game.seasonId) {
      return {
        minDate: toISODate(currentSeason.startDate),
        maxDate: toISODate(currentSeason.endDate),
      };
    }
    return { minDate: undefined, maxDate: undefined };
  }, [currentSeason, game]);

  const handleResultChange = useCallback((r: ScoreEntryResult) => setResult(r), []);

  const validate = (): string | null => {
    if (!isSlotComplete(slot)) {
      return 'Enter the date, time, venue and court/address for the game.';
    }
    if (isDateOutsideSeason(slot.date, seasonBounds.minDate, seasonBounds.maxDate)) {
      return `The date must fall within the season (${seasonBounds.minDate} – ${seasonBounds.maxDate}).`;
    }
    if (editScore && result) {
      if (result.retirement.isRetirement) {
        if (!result.retirement.retiredSide) return 'Please choose which team retired.';
        return null;
      }
      if (result.hasErrors) return 'Please fix the score validation errors.';
      if (!result.isComplete) return 'Enter the game scores — a winner must be determinable.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!game) return;
    setAttemptedSubmit(true);
    const err = validate();
    if (err) {
      setSubmitError(err);
      return;
    }
    setSubmitError(null);

    const data: AdminUpdateGameRequest = {
      scheduledDate: slot.date,
      scheduledTime: slot.time,
      venue: { name: slot.venueName, address: slot.venueAddress },
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };

    if (editScore && result) {
      const { isRetirement, retiredSide } = result.retirement;
      const retiredTeamId = isRetirement ? (retiredSide === 'a' ? teamAId : teamBId) : undefined;
      data.result = {
        scores: { [teamAId]: result.setWins.a, [teamBId]: result.setWins.b },
        sets: result.sets.map(s => ({ [teamAId]: s.a, [teamBId]: s.b })),
        ...(retiredTeamId ? { retiredTeamId } : {}),
      };
    }

    try {
      await dispatch(adminUpdateGame({ gameId: game.id, data })).unwrap();
      navigate(returnTo);
    } catch (submitErr) {
      setSubmitError(submitErr instanceof Error ? submitErr.message : String(submitErr));
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto mt-16 text-center">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access denied</h2>
          <p className="text-gray-600">You must be a League Administrator to edit games.</p>
        </div>
      </div>
    );
  }

  if (loading && !game) {
    return (
      <div className="flex items-center justify-center py-16">
        <svg
          className="animate-spin h-10 w-10 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-label="Loading"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  if (error && !game) {
    return (
      <div className="max-w-2xl mx-auto mt-8 rounded-md bg-danger-50 p-4" role="alert">
        <p className="text-sm text-danger-600">{error}</p>
      </div>
    );
  }

  if (!game) {
    return <div className="max-w-2xl mx-auto mt-16 text-center text-gray-500">Game not found.</div>;
  }

  const isDisputed = game.status === GameStatus.DISPUTED;

  return (
    <div className="max-w-2xl mx-auto">
      <Link to={returnTo} className="text-sm text-primary-600 hover:text-primary-700">
        &larr; Back
      </Link>
      <div className="mt-2 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Edit a game</h1>
          <p className="mt-1 text-gray-600">
            {teamALabel} <span className="text-gray-400">vs</span> {teamBLabel}
          </p>
        </div>
        <span className="shrink-0 text-xs px-2 py-1 rounded-full bg-violet-600 text-white">
          Admin
        </span>
      </div>

      {isDisputed && (
        <div className="mt-4 rounded-md bg-yellow-50 border border-yellow-200 p-4" role="status">
          <p className="text-sm font-semibold text-yellow-800">This result is disputed</p>
          <p className="mt-1 text-xs text-yellow-700">
            Correct the score (or confirm it as-is) and save to resolve the dispute — the game is
            then completed and points are recalculated.
          </p>
        </div>
      )}

      {game.notes && (
        <section className="mt-4 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">Notes history</h2>
          <p className="mt-2 text-sm text-gray-700 whitespace-pre-line">{game.notes}</p>
        </section>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {submitError && (
          <div className="mt-4 rounded-md bg-danger-50 p-4" role="alert">
            <p className="text-sm text-danger-600">{submitError}</p>
          </div>
        )}

        {/* Step 1: When & where */}
        <section className="mt-6 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">1 · When &amp; where</h2>
          <SlotEditor
            index={0}
            slot={slot}
            onChange={setSlot}
            showErrors={attemptedSubmit}
            minDate={seasonBounds.minDate}
            maxDate={seasonBounds.maxDate}
          />
        </section>

        {/* Step 2: Result */}
        <section className="mt-4 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700">2 · Result</h2>
          <label className="mt-3 flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={editScore}
              onChange={e => setEditScore(e.target.checked)}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded"
            />
            {game.result ? 'Edit / correct the score' : 'Record the result now'}
          </label>
          {editScore && (
            <div className="mt-3">
              <ScoreEntry
                pointSystem={game.divisionPointSystem}
                teamALabel={teamALabel}
                teamBLabel={teamBLabel}
                initialSets={initialSets}
                initialRetirement={initialRetirement}
                onChange={handleResultChange}
              />
            </div>
          )}
          {!editScore && (
            <p className="mt-2 text-xs text-gray-400">
              Leave unticked to change only the schedule/venue.
            </p>
          )}
        </section>

        {/* Step 3: Notes */}
        <GameNotesField
          label="3 · Notes"
          value={notes}
          onChange={setNotes}
          appendMode
          placeholder="e.g. Corrected game 2 after reviewing both teams' scoresheets."
        />

        {/* Actions */}
        <div className="mt-6 flex items-center justify-end gap-3">
          <Link
            to={returnTo}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading || (editScore && !result?.isComplete)}
            className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditGamePage;
